import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams, Routes, Route, Link } from 'react-router-dom';
import { getUserOrders, uploadMultipleFiles, uploadSingleFile } from '../services/api';
import api from '../services/api';
import {
  getAllPuzzleOptions,
  createPuzzleOption,
  updatePuzzleOption,
  deletePuzzleOption,
} from '../services/api';

const PUZZLE_TYPES = [
  { value: 'FULL_COURSE', label: 'Full Course' },
  { value: 'SINGLE_HOLE', label: 'Single Hole' },
  { value: 'SECTION', label: 'Section' },
  { value: 'FRONT_NINE', label: 'Front Nine' },
  { value: 'BACK_NINE', label: 'Back Nine' },
  { value: 'LOGO', label: 'Logo' },
];
const AVAILABILITY = [
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'COMING_SOON', label: 'Coming Soon' },
  { value: 'SOLD_OUT', label: 'Sold Out' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'EASY', label: 'Easy' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HARD', label: 'Hard' },
];

interface ProductForm {
  id?: string;
  title: string;
  description: string;
  price: string;
  puzzleType: string;
  availability: string;
  photos: File[];
  solutionImage: File | null;
  existingPhotos?: string[];
  existingSolutionImage?: string | null;
  height: string; // in mm
  width: string;  // in mm
  length: string; // in mm
  weight?: string; // in ounces
  pieces?: string;
  difficulty?: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
}

interface UserResponse {
  users: User[];
  total: number;
}

// Add PuzzleOption type for frontend
interface PuzzleOption {
  id: string;
  name: string;
  price: number;
  enabled: boolean;
  deleted: boolean;
  weight?: number;
}

const Admin: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState<'users' | 'products' | 'fulfillment'>('fulfillment');
  const [products, setProducts] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [shippingEnabled, setShippingEnabled] = useState(true);
  const itemsPerPage = 10;
  const [form, setForm] = useState<ProductForm>({
    title: '',
    description: '',
    price: '',
    puzzleType: PUZZLE_TYPES[0].value,
    availability: AVAILABILITY[0].value,
    photos: [],
    solutionImage: null,
    height: '',
    width: '',
    length: '',
    weight: '',
    pieces: '',
    difficulty: '',
  });
  const [editingProduct, setEditingProduct] = useState<ProductForm | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [orderLoading, setOrderLoading] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const ORDER_STATUSES = ['PENDING', 'PAID', 'SHIPPED', 'CANCELLED'];
  const [fulfillmentSearchTerm, setFulfillmentSearchTerm] = useState('');
  const [options, setOptions] = useState<PuzzleOption[]>([]); // For editing options
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [newOption, setNewOption] = useState<{ name: string; price: string; weight: string }>({ name: '', price: '', weight: '' });

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/login');
      return;
    }

    if (section === 'products') {
      fetchProducts();
      // Fetch initial shipping status
      api.get('/settings/shipping')
        .then(response => {
          setShippingEnabled(response.data.enabled);
        })
        .catch(error => {
          console.error('Error fetching shipping status:', error);
        });
    } else if (section === 'users') {
      fetchUsers();
    } else if (section === 'fulfillment') {
      fetchOrders();
    }
  }, [section, user, currentPage, searchTerm, selectedRole, orderStatusFilter, fulfillmentSearchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [section]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }

      const response = await api.get('/puzzles', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }

      const skip = (currentPage - 1) * itemsPerPage;
      const response = await api.get<UserResponse>('/users', {
        params: {
          skip,
          take: itemsPerPage,
          search: searchTerm || undefined,
          role: selectedRole || undefined,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setOrderLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await api.get('/orders', {
        params: {
          ...(orderStatusFilter ? { status: orderStatusFilter } : {}),
          ...(fulfillmentSearchTerm ? { search: fulfillmentSearchTerm } : {}),
          skip,
          take: itemsPerPage,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data.orders);
      setTotalOrders(response.data.total);
    } catch (e) {
      console.error('Error fetching orders:', e);
    } finally {
      setOrderLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (editingProduct) {
        setEditingProduct(prev => ({
          ...prev!,
          photos: Array.from(e.target.files!),
        }));
      } else {
        setForm(f => ({ ...f, photos: Array.from(e.target.files!) }));
      }
    }
  };

  const handleSolutionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      if (editingProduct) {
        setEditingProduct(prev => ({
          ...prev!,
          solutionImage: e.target.files![0],
        }));
      } else {
        setForm(f => ({ ...f, solutionImage: e.target.files![0] }));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (editingProduct) {
      setEditingProduct((prev) => prev ? { ...prev, [name]: value } : null);
    }
  };

  const handleEditProduct = async (product: any) => {
    setEditingProduct({
      id: product.id,
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      puzzleType: product.puzzleType,
      availability: product.availability,
      photos: [],
      solutionImage: null,
      existingPhotos: product.photos || [],
      existingSolutionImage: product.solutionImage,
      height: product.height?.toString() || '',
      width: product.width?.toString() || '',
      length: product.length?.toString() || '',
      weight: product.weight?.toString() || '',
      pieces: product.pieces?.toString() || '',
      difficulty: product.difficulty || '',
    });
    setShowForm(true);
    setOptionsLoading(true);
    try {
      const opts = await getAllPuzzleOptions(product.id);
      setOptions(opts);
    } catch (e) {
      setOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload new photos
      let photoPaths: string[] = [];
      const photosToUpload = editingProduct?.photos || form.photos;
      if (photosToUpload.length > 0) {
        try {
          const response = await uploadMultipleFiles(photosToUpload);
          photoPaths = response.filePaths;
        } catch (error) {
          console.error('Error uploading photos:', error);
          throw error;
        }
      }

      // Upload new solution image if present
      let solutionImagePath = null;
      const solutionImageToUpload = editingProduct?.solutionImage || form.solutionImage;
      if (solutionImageToUpload) {
        const response = await uploadSingleFile(solutionImageToUpload);
        solutionImagePath = response.filePath;
      }

      if (editingProduct) {
        // Update existing product
        await api.put(`/puzzles/${editingProduct.id}`, {
          title: editingProduct.title,
          description: editingProduct.description,
          price: parseFloat(editingProduct.price),
          puzzleType: editingProduct.puzzleType,
          availability: editingProduct.availability,
          photos: [...(editingProduct.existingPhotos || []), ...photoPaths],
          solutionImage: solutionImagePath || editingProduct.existingSolutionImage,
          height: parseFloat(editingProduct.height) || null,
          width: parseFloat(editingProduct.width) || null,
          length: parseFloat(editingProduct.length) || null,
          weight: editingProduct.weight ? parseFloat(editingProduct.weight) : null,
          pieces: editingProduct.pieces ? parseInt(editingProduct.pieces) : null,
          difficulty: editingProduct.difficulty || null,
        });
      } else {
        // Create new product
        await api.post('/puzzles', {
          title: form.title,
          description: form.description,
          price: parseFloat(form.price),
          puzzleType: form.puzzleType,
          availability: form.availability,
          photos: photoPaths,
          solutionImage: solutionImagePath,
          height: parseFloat(form.height) || null,
          width: parseFloat(form.width) || null,
          length: parseFloat(form.length) || null,
          weight: form.weight ? parseFloat(form.weight) : null,
          pieces: form.pieces ? parseInt(form.pieces) : null,
          difficulty: form.difficulty || null,
        });
      }

      // Refresh products list
      await fetchProducts();

      // Reset form
      setShowForm(false);
      setEditingProduct(null);
      setForm({
        title: '',
        description: '',
        price: '',
        puzzleType: PUZZLE_TYPES[0].value,
        availability: AVAILABILITY[0].value,
        photos: [],
        solutionImage: null,
        height: '',
        width: '',
        length: '',
        weight: '',
        pieces: '',
        difficulty: '',
      });
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      await api.delete(`/puzzles/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleUpdateUserRole = async (userId: string, newRole: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }

      await api.patch(`/users/${userId}`, 
        { role: newRole },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleRoleFilter = (role: string) => {
    setSelectedRole(role);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage);

  const updateOrderStatus = async (orderId: string, status: string) => {
    try {
      const token = localStorage.getItem('token');
      await api.patch(`/orders/${orderId}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      fetchOrders();
    } catch (e) {
      alert('Failed to update status');
    }
  };

  const sendShippedEmail = async (orderId: string) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/orders/${orderId}/send-shipped-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert('Shipped email sent');
    } catch (e) {
      alert('Failed to send shipped email');
    }
  };

  const viewShippingLabel = async (orderId: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/orders/${orderId}/shipping-label`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.shippingLabelUrl) {
        window.open(res.data.shippingLabelUrl, '_blank');
      } else {
        alert('No shipping label available');
      }
    } catch (e) {
      alert('Failed to fetch shipping label');
    }
  };

  const totalOrderPages = Math.ceil(totalOrders / itemsPerPage);

  const handleShippingToggle = async (enabled: boolean) => {
    try {
      await api.post('/settings/shipping', { enabled });
      setShippingEnabled(enabled);
    } catch (error) {
      console.error('Error updating shipping status:', error);
    }
  };

  const handleAddOption = async () => {
    if (!newOption.name || !newOption.price || !editingProduct?.id) return;
    setOptionsLoading(true);
    try {
      const created = await createPuzzleOption(editingProduct.id, {
        name: newOption.name,
        price: parseFloat(newOption.price),
        weight: newOption.weight ? parseFloat(newOption.weight) : undefined,
      });
      setOptions(prev => [...prev, created]);
      setNewOption({ name: '', price: '', weight: '' });
    } catch (e) {
      // handle error
    } finally {
      setOptionsLoading(false);
    }
  };
  const handleToggleOption = async (id: string) => {
    const opt = options.find(o => o.id === id);
    if (!opt) return;
    setOptionsLoading(true);
    try {
      const updated = await updatePuzzleOption(id, { enabled: !opt.enabled });
      setOptions(prev => prev.map(o => o.id === id ? updated : o));
    } catch (e) {}
    setOptionsLoading(false);
  };
  const handleDeleteOption = async (id: string) => {
    setOptionsLoading(true);
    try {
      await deletePuzzleOption(id);
      setOptions(prev => prev.filter(o => o.id !== id));
    } catch (e) {}
    setOptionsLoading(false);
  };
  const handleEditOption = async (id: string, field: 'name' | 'price' | 'weight', value: string) => {
    const opt = options.find(o => o.id === id);
    if (!opt) return;
    setOptionsLoading(true);
    try {
      const updated = await updatePuzzleOption(id, {
        [field]: field === 'price' || field === 'weight' ? parseFloat(value) : value,
      });
      setOptions(prev => prev.map(o => o.id === id ? updated : o));
    } catch (e) {}
    setOptionsLoading(false);
  };

  return (
    <div className="flex min-h-[80vh]">
      {/* Sidebar */}
      <aside className="w-64 bg-charcoal text-white flex flex-col py-8 px-4 space-y-4">
        <button
          className={`text-left px-4 py-2 rounded font-bold ${section === 'users' ? 'bg-orange text-white' : 'hover:bg-logo-gray'}`}
          onClick={() => setSection('users')}
        >
          User Management
        </button>
        <button
          className={`text-left px-4 py-2 rounded font-bold ${section === 'products' ? 'bg-orange text-white' : 'hover:bg-logo-gray'}`}
          onClick={() => setSection('products')}
        >
          Product Management
        </button>
        <button
          className={`text-left px-4 py-2 rounded font-bold ${section === 'fulfillment' ? 'bg-orange text-white' : 'hover:bg-logo-gray'}`}
          onClick={() => setSection('fulfillment')}
        >
          Order Fulfillment
        </button>
      </aside>
      {/* Main Content */}
      <main className="flex-1 p-8 bg-cream">
        {section === 'users' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">User Management</h2>
            
            {/* Search and Filter */}
            <div className="mb-6 flex gap-4 items-center">
              <form onSubmit={handleSearch} className="flex-1">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                />
              </form>
              <select
                value={selectedRole}
                onChange={(e) => handleRoleFilter(e.target.value)}
                className="border px-3 py-2 rounded"
              >
                <option value="">All Roles</option>
                <option value="USER">User</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            {loading ? (
              <div className="text-gray-500">Loading users...</div>
            ) : (
              <>
                <table className="w-full mb-8 bg-white rounded shadow">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Email</th>
                      <th className="p-2 text-left">Joined</th>
                      <th className="p-2 text-left">Order History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t">
                        <td className="p-2">{`${u.firstName} ${u.lastName}`}</td>
                        <td className="p-2">{u.email}</td>
                        <td className="p-2">{new Date(u.createdAt).toLocaleDateString()}</td>
                        <td className="p-2">
                          <a
                            href={`/admin/orders/${u.id}`}
                            className="text-logo-green hover:underline"
                          >
                            View Orders
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {section === 'products' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Product Management</h2>
            <div className="flex justify-between items-center mb-6">
              <button 
                className="bg-orange text-white px-4 py-2 rounded font-bold hover:bg-logo-gray" 
                onClick={() => {
                  setEditingProduct(null);
                  setShowForm(true);
                }}
              >
                Add Product
              </button>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={shippingEnabled}
                  onChange={(e) => handleShippingToggle(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>Enable Shipping</span>
              </label>
            </div>
            {/* Product List */}
            {loading ? (
              <div className="text-gray-500">Loading products...</div>
            ) : (
              <table className="w-full mb-8 bg-white rounded shadow">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Price</th>
                    <th className="p-2 text-left">Availability</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="p-2">{p.title}</td>
                      <td className="p-2">{PUZZLE_TYPES.find(t => t.value === p.puzzleType)?.label || p.puzzleType}</td>
                      <td className="p-2">${p.price}</td>
                      <td className="p-2">{AVAILABILITY.find(a => a.value === p.availability)?.label || p.availability}</td>
                      <td className="p-2">
                        <button
                          onClick={() => handleEditProduct(p)}
                          className="text-orange hover:text-logo-gray mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(p.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* Add/Edit Product Form */}
            {showForm && (
              <form className="bg-white p-6 rounded shadow max-w-xl" onSubmit={handleAddProduct}>
                <h3 className="text-xl font-bold mb-4">{editingProduct ? 'Edit Product' : 'Add Product'}</h3>
                <div className="mb-4">
                  <label className="block mb-1">Course Name</label>
                  <input 
                    name="title" 
                    value={editingProduct?.title || form.title} 
                    onChange={handleInputChange} 
                    className="w-full border px-3 py-2 rounded" 
                    required 
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Description</label>
                  <textarea 
                    name="description" 
                    value={editingProduct?.description || form.description} 
                    onChange={handleInputChange} 
                    className="w-full border px-3 py-2 rounded" 
                    required 
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Price</label>
                  <input 
                    name="price" 
                    type="number" 
                    value={editingProduct?.price || form.price} 
                    onChange={handleInputChange} 
                    className="w-full border px-3 py-2 rounded" 
                    required 
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Puzzle Type</label>
                  <select 
                    name="puzzleType" 
                    value={editingProduct?.puzzleType || form.puzzleType} 
                    onChange={handleInputChange} 
                    className="w-full border px-3 py-2 rounded"
                  >
                    {PUZZLE_TYPES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Availability</label>
                  <select 
                    name="availability" 
                    value={editingProduct?.availability || form.availability} 
                    onChange={handleInputChange} 
                    className="w-full border px-3 py-2 rounded"
                  >
                    {AVAILABILITY.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Difficulty</label>
                  <select
                    name="difficulty"
                    value={editingProduct?.difficulty || form.difficulty}
                    onChange={handleInputChange}
                    className="w-full border px-3 py-2 rounded"
                  >
                    <option value="">Select difficulty</option>
                    {DIFFICULTY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block mb-1">Height (mm)</label>
                    <input 
                      name="height" 
                      type="number" 
                      step="0.1"
                      value={editingProduct?.height || form.height} 
                      onChange={handleInputChange} 
                      className="w-full border px-3 py-2 rounded" 
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Width (mm)</label>
                    <input 
                      name="width" 
                      type="number" 
                      step="0.1"
                      value={editingProduct?.width || form.width} 
                      onChange={handleInputChange} 
                      className="w-full border px-3 py-2 rounded" 
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Length (mm)</label>
                    <input 
                      name="length" 
                      type="number" 
                      step="0.1"
                      value={editingProduct?.length || form.length} 
                      onChange={handleInputChange} 
                      className="w-full border px-3 py-2 rounded" 
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Weight (oz)</label>
                  <input
                    name="weight"
                    type="number"
                    step="0.1"
                    value={editingProduct?.weight || form.weight}
                    onChange={handleInputChange}
                    className="w-full border px-3 py-2 rounded"
                    placeholder="e.g. 16"
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Number of Pieces</label>
                  <input
                    type="number"
                    name="pieces"
                    value={editingProduct?.pieces || form.pieces}
                    onChange={handleInputChange}
                    className="w-full border px-3 py-2 rounded"
                    min="0"
                    placeholder="e.g. 1000"
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Product Photos</label>
                  {editingProduct?.existingPhotos && editingProduct.existingPhotos.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm text-gray-600 mb-2">Existing Photos:</p>
                      <div className="flex flex-wrap gap-2">
                        {editingProduct.existingPhotos.map((photo, idx) => (
                          <div key={idx} className="relative">
                            <img src={photo} alt={`Product ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                            <button
                              type="button"
                              onClick={() => {
                                setEditingProduct(prev => ({
                                  ...prev!,
                                  existingPhotos: prev!.existingPhotos!.filter((_, i) => i !== idx)
                                }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <input type="file" multiple accept="image/*" onChange={handleFileChange} className="w-full" />
                  {(editingProduct?.photos.length || form.photos.length) > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Array.from(editingProduct?.photos || form.photos).map((file, idx) => (
                        <span key={idx} className="text-xs bg-gray-200 px-2 py-1 rounded">{file.name}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block mb-1">Solution Image</label>
                  {editingProduct?.existingSolutionImage && (
                    <div className="mb-2">
                      <p className="text-sm text-gray-600 mb-2">Existing Solution Image:</p>
                      <div className="relative inline-block">
                        <img 
                          src={editingProduct.existingSolutionImage} 
                          alt="Solution" 
                          className="w-20 h-20 object-cover rounded" 
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setEditingProduct(prev => ({
                              ...prev!,
                              existingSolutionImage: null
                            }));
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleSolutionChange} className="w-full" />
                  {(editingProduct?.solutionImage || form.solutionImage) && (
                    <div className="mt-2 text-xs bg-gray-200 px-2 py-1 rounded">
                      {(editingProduct?.solutionImage || form.solutionImage)?.name}
                    </div>
                  )}
                </div>
                {/* Customization Options Section */}
                {editingProduct && (
                  <div className="mb-6">
                    <h4 className="text-lg font-bold mb-2">Customization Options</h4>
                    {optionsLoading ? (
                      <div className="text-logo-gray">Loading options...</div>
                    ) : (
                      <>
                        <div className="mb-2">
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              placeholder="Option name"
                              value={newOption.name}
                              onChange={e => setNewOption(opt => ({ ...opt, name: e.target.value }))}
                              className="border px-2 py-1 rounded flex-1"
                            />
                            <input
                              type="number"
                              placeholder="Price"
                              value={newOption.price}
                              onChange={e => setNewOption(opt => ({ ...opt, price: e.target.value }))}
                              className="border px-2 py-1 rounded w-24"
                            />
                            <input
                              type="number"
                              placeholder="Weight (oz)"
                              value={newOption.weight}
                              onChange={e => setNewOption(opt => ({ ...opt, weight: e.target.value }))}
                              className="border px-2 py-1 rounded w-24"
                            />
                            <button type="button" onClick={handleAddOption} className="bg-logo-green text-white px-3 py-1 rounded">Add</button>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="p-1 text-left">Name</th>
                              <th className="p-1 text-left">Price</th>
                              <th className="p-1 text-left">Weight (oz)</th>
                              <th className="p-1 text-left">Enabled</th>
                              <th className="p-1 text-left">Delete</th>
                            </tr>
                          </thead>
                          <tbody>
                            {options.filter(opt => !opt.deleted).map(opt => (
                              <tr key={opt.id}>
                                <td className="p-1">
                                  <input
                                    type="text"
                                    value={opt.name}
                                    onChange={e => handleEditOption(opt.id, 'name', e.target.value)}
                                    className="border px-2 py-1 rounded w-full"
                                  />
                                </td>
                                <td className="p-1">
                                  <input
                                    type="number"
                                    value={opt.price}
                                    onChange={e => handleEditOption(opt.id, 'price', e.target.value)}
                                    className="border px-2 py-1 rounded w-20"
                                  />
                                </td>
                                <td className="p-1">
                                  <input
                                    type="number"
                                    value={opt.weight || ''}
                                    onChange={e => handleEditOption(opt.id, 'weight', e.target.value)}
                                    className="border px-2 py-1 rounded w-20"
                                  />
                                </td>
                                <td className="p-1">
                                  <button type="button" onClick={() => handleToggleOption(opt.id)} className={opt.enabled ? 'text-logo-green' : 'text-logo-gray'}>
                                    {opt.enabled ? 'Enabled' : 'Disabled'}
                                  </button>
                                </td>
                                <td className="p-1">
                                  <button type="button" onClick={() => handleDeleteOption(opt.id)} className="text-red-500">Delete</button>
                                </td>
                              </tr>
                            ))}
                            {options.filter(opt => !opt.deleted).length === 0 && (
                              <tr><td colSpan={5} className="text-logo-gray text-center py-2">No options yet.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                )}
                <div className="flex gap-4 mt-6">
                  <button type="submit" className="bg-orange text-white px-4 py-2 rounded font-bold hover:bg-logo-gray">
                    {editingProduct ? 'Update' : 'Save'}
                  </button>
                  <button 
                    type="button" 
                    className="bg-logo-gray text-white px-4 py-2 rounded font-bold" 
                    onClick={() => {
                      setShowForm(false);
                      setEditingProduct(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
        {section === 'fulfillment' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Order Fulfillment</h2>
            <div className="mb-4 flex gap-4 items-center">
              <form onSubmit={e => { e.preventDefault(); fetchOrders(); }} className="flex-1">
                <input
                  type="text"
                  placeholder="Search by email, name, or order ID..."
                  value={fulfillmentSearchTerm}
                  onChange={e => setFulfillmentSearchTerm(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                />
              </form>
              <label>Filter by status:</label>
              <select value={orderStatusFilter} onChange={e => { setOrderStatusFilter(e.target.value); setCurrentPage(1); }} className="border px-3 py-2 rounded">
                <option value="">All</option>
                {ORDER_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
            {orderLoading ? (
              <div className="text-gray-500">Loading orders...</div>
            ) : (
              <>
                <table className="w-full mb-8 bg-white rounded shadow">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">Order Date</th>
                      <th className="p-2 text-left">Email Address</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Order ID</th>
                      <th className="p-2 text-left">Total</th>
                      <th className="p-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => (
                      <tr key={order.id} className="border-t">
                        <td className="p-2">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}</td>
                        <td className="p-2">{order.customerEmail || order.user?.email}</td>
                        <td className="p-2">{order.customerName || (order.user?.firstName + ' ' + order.user?.lastName)}</td>
                        <td className="p-2">
                          <select value={order.status} onChange={e => updateOrderStatus(order.id, e.target.value)} className="border rounded px-2 py-1">
                            {ORDER_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                          </select>
                        </td>
                        <td className="p-2">{order.id}</td>
                        <td className="p-2">${order.total?.toFixed(2)}</td>
                        <td className="p-2 flex gap-2">
                          <button className="text-logo-green underline" onClick={() => viewShippingLabel(order.id)}>Shipping Label</button>
                          {/* <button className="text-orange underline" onClick={() => sendShippedEmail(order.id)}>Send Shipped Email</button> */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                {totalOrderPages > 1 && (
                  <div className="flex justify-center gap-2 mb-8">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1">
                      Page {currentPage} of {totalOrderPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalOrderPages, p + 1))}
                      disabled={currentPage === totalOrderPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// Order History Page
const UserOrderHistory: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!userId) return;
        const data = await getUserOrders(userId);
        setOrders(data);
      } catch (err) {
        setError('Failed to load orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [userId]);

  if (loading) return <div className="text-center p-8">Loading order history...</div>;
  if (error) return <div className="text-center p-8 text-logo-red">{error}</div>;

  return (
    <div className="max-w-3xl mx-auto p-8 bg-white rounded shadow">
      <h2 className="text-2xl font-bold mb-6">Order History</h2>
      <Link to="/admin" className="text-logo-green hover:underline mb-4 inline-block">&larr; Back to Admin</Link>
      {orders.length === 0 ? (
        <div className="text-logo-gray">No orders found for this user.</div>
      ) : (
        <table className="w-full mb-8 bg-white rounded shadow">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Order ID</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Total</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Items</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id} className="border-t">
                <td className="p-2">{order.id}</td>
                <td className="p-2">{new Date(order.createdAt).toLocaleDateString()}</td>
                <td className="p-2">${order.total?.toFixed(2)}</td>
                <td className="p-2">{order.status}</td>
                <td className="p-2">
                  <ul className="list-disc ml-4">
                    {order.items?.map((item: any) => (
                      <li key={item.id}>{item.puzzle?.title} x {item.quantity}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Replace the export default with a Routes wrapper
const AdminRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<Admin />} />
    <Route path="/orders/:userId" element={<UserOrderHistory />} />
  </Routes>
);

export default AdminRoutes; 